/**
 * Canonical query keys for every server resource web-v2 caches.
 *
 * Two rules:
 *   1. Every hook that fetches a resource imports its key from here — never
 *      inlines a string. If the shape drifts, invalidation still works
 *      because `queryClient.invalidateQueries({ queryKey: qk.reviews.all })`
 *      catches every consumer of that key branch.
 *   2. Keys are shaped as tuples with a scope prefix + narrowing selectors,
 *      matching TanStack Query's `queryKey` matching semantics — invalidating
 *      `["reviews"]` cascades to `["reviews", "list"]` and `["reviews", 42]`.
 *
 * When the backend adds pagination / sort / filter params, add them as the
 * last element of the list key so switching pages invalidates only that
 * page, not the whole resource.
 */
export const qk = {
  clients: {
    all: ["clients"] as const,
    list: (params?: object) => [...qk.clients.all, "list", params ?? {}] as const,
    detail: (id: string | number) => [...qk.clients.all, "detail", String(id)] as const,
  },
  users: {
    all: ["users"] as const,
    list: (params?: object) => [...qk.users.all, "list", params ?? {}] as const,
    detail: (id: string | number) => [...qk.users.all, "detail", String(id)] as const,
    me: () => [...qk.users.all, "me"] as const,
  },
  apiKeys: {
    all: ["api-keys"] as const,
    list: () => [...qk.apiKeys.all, "list"] as const,
  },
  threads: {
    all: ["threads"] as const,
    list: (params: object) => [...qk.threads.all, "list", params] as const,
  },
  reviews: {
    all: ["reviews"] as const,
    list: (filter?: object) => [...qk.reviews.all, "list", filter ?? {}] as const,
    detail: (id: string | number) => [...qk.reviews.all, "detail", String(id)] as const,
    people: (id: string | number) => [...qk.reviews.all, "detail", String(id), "people"] as const,
    comments: (id: string | number) => [...qk.reviews.all, "detail", String(id), "comments"] as const,
    history: (id: string | number) => [...qk.reviews.all, "detail", String(id), "history"] as const,
    alignment: (id: string | number) => [...qk.reviews.all, "detail", String(id), "alignment"] as const,
    documents: (id: string | number) => [...qk.reviews.all, "detail", String(id), "documents"] as const,
  },
} as const
